import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, useSubscription } from '@apollo/client';
import {
  SUBSCRIBE_OPEN_LABOR_TICKETS_FOR_CONTROL_BAR,
  GET_PROGRAM_EVENTS_GROUPED,
} from 'lib/api/queries';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getIsSetupModeActive } from 'lib/selectors/getIsSetupModeActive';
import {
  filterProgramEventsByPerson,
  normalizePersonId,
} from 'lib/utils/apmPersonScope';
import {
  actionSetCurrentWorkOrderOp,
  actionSetLaborTickets,
  actionSetEventsLoading,
  actionSetJobCardCount,
  actionSetHasCards,
  actionSetHasUnmatchedCards,
  actionSetHasOpenLaborTickets,
  actionSetProgramEvents,
} from 'lib/actions';

const POLL_INTERVAL_MS = 10_000;

/**
 * Fetches program events (via Apollo REST) and open labor tickets (GraphQL subscription)
 * so the ERPMultiJobControlBar knows whether to show "Submit All" or
 * "Select Jobs" — even when the apm-opdash-display MFE is not mounted
 * (e.g. the user is on a different tab after a page refresh).
 *
 * Also dispatches currentJob info to Redux so the header and other selectors
 * work correctly regardless of which tab is active.
 *
 * Returns { refetch, events, eventsLoading }.
 */
export const useProgramEventDisplayState = () => {
  const dispatch = useDispatch();
  const enabled = useSelector(getIsApmERPJobFlow);
  const isSetupModeActive = useSelector(getIsSetupModeActive);
  const machine = useSelector(getMachine);
  const scopeStart = useSelector(getScopeStart);
  const latestOperator = useSelector(getLatestOperator);
  const personErpId = normalizePersonId(latestOperator?.erpId);
  const machineRef = machine?.machineRef;
  const erpResourceId = machine?.erpResourceId;

  const skip = !enabled || !machineRef || !scopeStart || !personErpId;

  // ---- Program events (Apollo REST poll) ----
  const {
    data: eventsData,
    loading: eventsLoading,
    refetch: refetchEvents,
  } = useQuery(GET_PROGRAM_EVENTS_GROUPED, {
    variables: { machineRef, start: scopeStart },
    skip,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const events = useMemo(() => {
    if (skip) return [];
    return filterProgramEventsByPerson(
      eventsData?.programEventsGrouped || [],
      personErpId
    );
  }, [eventsData, personErpId, skip]);

  // Apollo sets loading=true on every poll/refetch (network-only + notifyOnNetworkStatusChange).
  // Only treat the initial empty fetch as "loading" so the opdash does not flash on each poll.
  const showEventsLoading = eventsLoading && events.length === 0;

  // Dispatch loading state to Redux for components that check it.
  useEffect(() => {
    dispatch(actionSetEventsLoading(showEventsLoading));
  }, [showEventsLoading, dispatch]);

  // Re-fetch when OperatorView signals a refresh (e.g. after job selection).
  const refetchRef = useRef(refetchEvents);
  refetchRef.current = refetchEvents;
  useEffect(() => {
    if (skip) return undefined;
    const handler = () => {
      return refetchRef.current();
    };
    window.addEventListener('apm:opdash-refresh', handler);
    return () => {
      return window.removeEventListener('apm:opdash-refresh', handler);
    };
  }, [skip]);

  // ---- Open labor tickets (GraphQL subscription) ----
  const ltWhere = useMemo(() => {
    const conditions = [
      { machineRef: { _eq: machineRef } },
      { clockOut: { _is_null: true } },
    ];
    if (personErpId) {
      conditions.push({ personId: { _eq: personErpId } });
    }
    return { _and: conditions };
  }, [machineRef, personErpId]);

  const { data: ltData, loading: ltLoading } = useSubscription(
    SUBSCRIBE_OPEN_LABOR_TICKETS_FOR_CONTROL_BAR,
    {
      variables: { where: ltWhere },
      skip: !enabled || !machineRef || !personErpId,
    }
  );

  const openLaborTickets = useMemo(() => {
    return ltData?.erpLaborTickets ?? [];
  }, [ltData]);

  // ---- Derived state ----
  const {
    hasCards,
    hasUnmatchedCards,
    hasOpenLaborTickets,
    currentJob,
    cardCount,
  } = useMemo(() => {
    const openLtRefs = new Set(
      openLaborTickets.map((lt) => {
        return lt.laborTicketRef;
      })
    );

    // Visible events: exclude confirmed events whose LT is no longer open
    const visibleEvents = events.filter((event) => {
      if (event.confirmedAt && event.laborTicketRef) {
        return openLtRefs.has(event.laborTicketRef);
      }
      return true;
    });

    const visibleEventWooRefs = new Set(
      visibleEvents
        .filter((e) => {
          return e.workOrderOperationRef != null;
        })
        .map((e) => {
          return Number(e.workOrderOperationRef);
        })
    );

    // Standalone open LTs not covered by an event
    const standaloneLts = openLaborTickets.filter((lt) => {
      return (
        lt.workOrderOperation?.workOrderOperationRef != null &&
        !visibleEventWooRefs.has(lt.workOrderOperation.workOrderOperationRef)
      );
    });

    const matchedEvents = visibleEvents.filter((e) => {
      return e.workOrderOperationRef != null;
    });

    const setupOpenLts = openLaborTickets.filter((lt) => {
      return lt.type === 'setup';
    });

    const cards = isSetupModeActive
      ? setupOpenLts.length > 0
      : matchedEvents.length > 0 || standaloneLts.length > 0;
    const unmatchedCards = visibleEvents.some((e) => {
      return e.workOrderOperationRef == null;
    });
    const count = isSetupModeActive
      ? setupOpenLts.length
      : matchedEvents.length + standaloneLts.length;

    // Header represents the active job: only the most recent event counts.
    // If the most recent visible event's WOO is closed, nothing is active.
    const mostRecent = visibleEvents[0] ?? null;
    const mostRecentIsActive =
      !isSetupModeActive && mostRecent && !mostRecent.closedDate;

    let job = null;
    if (isSetupModeActive) {
      job = null;
    } else if (mostRecentIsActive) {
      const activeEvent = mostRecent;
      if (activeEvent.workOrderOperationRef != null) {
        const partOp = activeEvent.candidatePartOps?.[0];
        job = {
          workOrderOperationRef: activeEvent.workOrderOperationRef,
          workOrderId: activeEvent.workOrderId ?? null,
          partNumber: activeEvent.partNumber ?? partOp?.partNumber ?? null,
          sequenceNumber:
            activeEvent.sequenceNumber ?? partOp?.sequenceNumber ?? null,
          type: 'production',
          clockIn: activeEvent.eventTime,
          displayState: activeEvent.confirmedAt ? 'CONFIRMED' : 'SUGGESTED',
        };
      } else if (standaloneLts.length > 0) {
        const lt = standaloneLts[0];
        job = {
          workOrderOperationRef:
            lt.workOrderOperation?.workOrderOperationRef ?? null,
          workOrderId: lt.workOrderId,
          partNumber: lt.workOrderOperation?.workOrder?.partNumber ?? null,
          sequenceNumber: lt.sequenceNumber,
          type: lt.type || 'production',
          clockIn: lt.clockIn,
          displayState: 'WAITING_FOR_PROGRAM',
        };
      }
    } else if (standaloneLts.length > 0) {
      const lt = standaloneLts[0];
      job = {
        workOrderOperationRef:
          lt.workOrderOperation?.workOrderOperationRef ?? null,
        workOrderId: lt.workOrderId,
        partNumber: lt.workOrderOperation?.workOrder?.partNumber ?? null,
        sequenceNumber: lt.sequenceNumber,
        type: lt.type || 'production',
        clockIn: lt.clockIn,
        displayState: 'WAITING_FOR_PROGRAM',
      };
    }

    return {
      hasCards: cards,
      hasUnmatchedCards: unmatchedCards,
      hasOpenLaborTickets: openLaborTickets.length > 0,
      currentJob: job,
      cardCount: count,
    };
  }, [events, openLaborTickets, isSetupModeActive]);

  // ---- Redux dispatches (only when values actually change) ----
  const showLtLoading = ltLoading && openLaborTickets.length === 0;
  const loading = showEventsLoading || showLtLoading;
  const prevJobKey = useRef('');

  useEffect(() => {
    if (!enabled) return;
    if (loading) return;

    const key = JSON.stringify(currentJob);
    if (key === prevJobKey.current) return;
    prevJobKey.current = key;

    if (currentJob) {
      dispatch(
        actionSetCurrentWorkOrderOp({
          workOrderOperationRef: currentJob.workOrderOperationRef,
          workOrderId: currentJob.workOrderId,
          sequenceNumber: currentJob.sequenceNumber,
          workOrder: { partNumber: currentJob.partNumber },
        })
      );
      // Set a synthetic labor ticket so getJobStatus / getHasOpenJob work
      dispatch(
        actionSetLaborTickets([
          {
            laborTicketRef: null,
            state: 'OPEN',
            type: currentJob.type || 'production',
            clockIn: currentJob.clockIn,
            clockOut: null,
            workOrderId: currentJob.workOrderId,
            sequenceNumber: currentJob.sequenceNumber,
            resourceId: erpResourceId,
            workOrderOperation: {
              workOrderOperationRef: currentJob.workOrderOperationRef,
            },
          },
        ])
      );
    } else {
      dispatch(actionSetCurrentWorkOrderOp(null));
      dispatch(actionSetLaborTickets([]));
    }
  }, [enabled, currentJob, loading, dispatch, erpResourceId]);

  useEffect(() => {
    if (!enabled) return;
    if (!loading) {
      dispatch(actionSetJobCardCount(cardCount));
      dispatch(actionSetHasCards(hasCards));
      dispatch(actionSetHasUnmatchedCards(hasUnmatchedCards));
      dispatch(actionSetHasOpenLaborTickets(hasOpenLaborTickets));
      dispatch(actionSetProgramEvents(events));
    }
  }, [
    enabled,
    cardCount,
    hasCards,
    hasUnmatchedCards,
    hasOpenLaborTickets,
    events,
    loading,
    dispatch,
  ]);

  return { refetch: refetchEvents, events, eventsLoading: showEventsLoading };
};
